import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ModalShell from "./ModalShell";
import FileDropzone from "./FileDropzone";
import { Collection } from "../../../api/derin";
import { collectionColors, font, subtitleSx } from "./tokens";

interface ConfigFileModalProps {
  open: boolean;
  collection: Collection | null;
  onClose: () => void;
  onSubmit: (file: File) => void;
  isSubmitting?: boolean;
}

/**
 * The "+ Configuration File" action. A file of its own — not a survey: it controls how
 * surveys and questions are presented to the client. It is not versioned, so uploading
 * replaces whatever file is currently applied.
 */
const ConfigFileModal: React.FC<ConfigFileModalProps> = ({
  open,
  collection,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [file, setFile] = useState<File | null>(null);

  const close = () => {
    setFile(null);
    onClose();
  };

  const currentFileName = collection?.configFileName ?? null;

  return (
    <ModalShell
      open={open}
      onClose={close}
      title="Configuration file"
      subtitle="Controls how surveys and questions are presented to the client. This file is not versioned — uploading replaces the one currently applied."
      confirmLabel={currentFileName ? "Replace file" : "Upload file"}
      onConfirm={() => {
        if (file) {
          onSubmit(file);
        }
      }}
      confirmDisabled={!file}
      isSubmitting={isSubmitting}
    >
      {collection && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "12px",
            background: collectionColors.surfaceLight,
            borderRadius: "8px",
          }}
        >
          <DescriptionOutlinedIcon sx={{ width: 16, height: 16, color: collectionColors.textSecondary }} />
          <Box>
            <Typography sx={{ ...subtitleSx, fontSize: "12px", lineHeight: "16px" }}>
              Currently applied
            </Typography>
            <Typography
              sx={{
                fontFamily: font,
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                color: currentFileName ? collectionColors.textPrimary : collectionColors.textSecondary,
              }}
            >
              {currentFileName ?? "No configuration file yet"}
            </Typography>
          </Box>
        </Box>
      )}

      <FileDropzone file={file} onSelect={setFile} placeholder="Click to upload or drag and drop" />
    </ModalShell>
  );
};

export default ConfigFileModal;
